import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { RoutineRecordInfo } from "./routineRecordSlice";
import { routineRecordChange } from "./routineRecordChangeSlice";

export interface RoutineRecordListState {
    state: LoadStateInfo;
    request?: RetrieveRoutineRecordListRequest;
    list?: RoutineRecordInfo[];
}

const initialState: RoutineRecordListState = {
    state: notStarted(),
};


export interface RetrieveRoutineRecordListRequest {
    routineId: string;
    eventDate: string;
    classroomId: string;
}
export const retrieveRoutineRecordList = createAsyncThunk(
    "retrieveRoutineRecordList",
    async (param: RetrieveRoutineRecordListRequest, thunkAPI) => {
        console.debug(`retrieve routine records for ${param.routineId}, classroom ${param.classroomId}, date ${param.eventDate}`);

        const response = await fetch(`${getBackendUrl()}/api/routine/${param.routineId}/records/${param.eventDate}/${param.classroomId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveRoutineRecordListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.routineRecordList;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveRoutineRecordList skipped: ${status.state.state}`);
            return false;
        }
    }
);


export const routineRecordListSlice = createSlice({
    name: "routineRecordList",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        check: (state, action: PayloadAction<RetrieveRoutineRecordListRequest>) => {
            if (state.state.state != LoadState.NotStarted
                && (state.request && state.request.routineId != action.payload.routineId)
            ) {
                console.debug(`routine item list not retrieved for routine ${action.payload.routineId}`);
                state.state = notStarted();
                state.request = action.payload;
                state.list = undefined;
            }
        },
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveRoutineRecordList.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrieveRoutineRecordList.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
                // state.list?.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
            })
            .addCase(retrieveRoutineRecordList.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(routineRecordChange.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                const payload: RoutineRecordInfo = action.payload;
                if (state.list) {
                    const index = state.list.findIndex((r) => r.id === payload.id);
                    if (index >= 0) {
                        state.list[index] = payload;
                    } else {
                        state.list.push(payload);
                    }
                }
            })
            ;
    },
});

// export const { } = routineRecordListSlice.actions;

export const selectRoutineRecordList = (state: RootState) => state.routineRecordList;

export default routineRecordListSlice.reducer;
